/*
 * Developed by CQ Inversiones SAS.  Copyright (c) 2025. All rights reserved.
 * Desarrollado por CQ Inversiones SAS.  Copyright (c) 2025. Todos los derechos reservados.
 */

/*
 * Project:      mkad-frontend
 * Developed by: Juan David Pelaez Cumbe
 * Date:         30/04/25 - 07:53:20
 * Module name:  mkad-breadcrumb.store
 * File name:    mkad-breadcrumb.store.ts
 * IDE:          WebStorm
 */


import {
  MkadMenuItemModel,
} from '@shared/components/global/molecules/breadcrumb/breadcrumb-model.interface';
import {patchState, signalStore, withComputed, withHooks, withMethods, withState} from '@ngrx/signals';
import {computed, inject} from '@angular/core';
import {ZbSecureStorage} from '@zibanu/lib';
import {BREADCRUMB_STORAGE_KEY} from '@core/constants/breadcrumb-storage-key.const';
import {PreferencesService} from '@shared/services/main/customization/preferences.service';
import {UserCustomerDataInterface} from '@shared/interface';

export const breadcrumbHome: MkadMenuItemModel = {
  label: '',
  icon: 'fa-home',
  tooltip: 'app.header.breadcrumbs.home',
};

export interface BreadcrumbState {
  data: MkadMenuItemModel[];
  loading: boolean;
  home: MkadMenuItemModel;
}

export const breadcrumbInitialState: BreadcrumbState = {
  data: [],
  loading: false,
  home: breadcrumbHome,
};

export const BreadcrumbStore = signalStore(
  {providedIn: 'root'},
  withState(breadcrumbInitialState),
  withComputed((store) => ({
    homeItem: computed(() => store.home()),
    items: computed(() => store.data()),
  })),
  withMethods((store,
               preferences = inject(PreferencesService),
  ) => {
    const storage = new ZbSecureStorage();

    const overridePatchState = (newState: Partial<BreadcrumbState>): void => {
      patchState(store, newState);
      storage.setItem(BREADCRUMB_STORAGE_KEY, JSON.stringify({
        data: store.items(),
        loading: false,
        home: store.home(),
      }));
    };

    function addSlot(slot: MkadMenuItemModel): void {
      const currentData: MkadMenuItemModel[] = store.data();
      const newData = currentData.concat([slot]);
      if (currentData.length > 0) {
        overridePatchState({data: newData});
      } else {
        const homeData = store.home();
        const customerData: UserCustomerDataInterface | null = preferences.userCustomer();
        if (customerData) {
          overridePatchState({
            data: newData,
            home: {...homeData, data: customerData}
          });
        }
      }
    }

    function relocateItems(selectedIndex: number): void {
      const items: MkadMenuItemModel[] = [...store.data()];
      items.splice((selectedIndex + 1), items.length);
      patchState(store, {data: items});
    }

    const cleanStoreData = (): void => {
      patchState(store, breadcrumbInitialState);
      storage.removeItem(BREADCRUMB_STORAGE_KEY);
    };

    return {
      addSlot,
      cleanStoreData,
      relocateItems,
    };
  }),
  withHooks({
    onInit(store) {
      const storage = new ZbSecureStorage();
      const storeData: string | null = storage.getItem(BREADCRUMB_STORAGE_KEY);
      if (storeData === null) {
        patchState(store, breadcrumbInitialState);
      } else {
        patchState(store, JSON.parse(storeData));
      }
    },
  }),
);
